import { useEffect, useState } from 'react';
import { PageHeader, Panel, Toolbar, KeyValueList } from '../components/Page';
import { EmptyState, ErrorState, InlineError, CardSkeleton } from '../components/Feedback';
import { CheckboxField, SelectField, SliderField, TextField } from '../components/Form';
import { TableWrap } from '../components/Table';
import { SortableTh } from '../components/SortableTh';
import { RequireRole } from '../lib/auth';
import { emailIntake } from '../lib/endpoints';
import { useAsync } from '../lib/hooks';
import { useSortableTable } from '../lib/useSortableTable';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/api';
import { GatewayRole } from '../lib/enums';
import { formatDateTime, formatRelative } from '../lib/format';
import type {
  EmailIntakeApprovedRuleDto,
  EmailIntakeForwarderDto,
  EmailIntakeCompanyAliasDto,
  EmailIntakeHeldInvoiceDto,
  EmailIntakePendingSenderDto,
  EmailIntakeStatusDto,
} from '../lib/types';

type ForwarderColumn = 'address' | 'label' | 'added';

function forwarderSortValue(f: EmailIntakeForwarderDto, key: ForwarderColumn): string | number {
  switch (key) {
    case 'address':
      return f.emailAddress;
    case 'label':
      return f.label ?? '';
    case 'added':
      return f.createdUtc;
    default:
      return '';
  }
}

type ApprovedRuleColumn = 'match' | 'value' | 'vendor' | 'added';

function approvedRuleSortValue(rule: EmailIntakeApprovedRuleDto, key: ApprovedRuleColumn): string | number {
  switch (key) {
    case 'match':
      return rule.matchType;
    case 'value':
      return rule.matchValue;
    case 'vendor':
      return rule.vendorName || rule.qboVendorId;
    case 'added':
      return rule.createdUtc;
    default:
      return '';
  }
}

type HeldInvoiceColumn = 'invoice' | 'vendor' | 'addressedTo' | 'amount' | 'received';

function heldInvoiceSortValue(item: EmailIntakeHeldInvoiceDto, key: HeldInvoiceColumn): string | number {
  switch (key) {
    case 'invoice':
      return item.invoiceNumber || item.fileName;
    case 'vendor':
      return item.vendorName ?? '';
    case 'addressedTo':
      return item.billToName ?? '';
    case 'amount':
      return item.invoiceTotal ?? 0;
    case 'received':
      return item.receivedUtc ?? '';
    default:
      return '';
  }
}

type AliasColumn = 'company' | 'address' | 'note' | 'added';

function aliasSortValue(alias: EmailIntakeCompanyAliasDto, key: AliasColumn): string | number {
  switch (key) {
    case 'company':
      return alias.name ?? '';
    case 'address':
      return alias.addressContains ?? '';
    case 'note':
      return alias.note ?? '';
    case 'added':
      return alias.createdUtc;
    default:
      return '';
  }
}

type PendingSenderColumn = 'from' | 'domain' | 'subject' | 'received' | 'vendor';

function pendingSenderSortValue(row: EmailIntakePendingSenderDto, key: PendingSenderColumn): string | number {
  switch (key) {
    case 'from':
      return row.fromAddress;
    case 'domain':
      return row.domain;
    case 'subject':
      return row.subject;
    case 'received':
      return row.receivedUtc ?? '';
    case 'vendor':
      return row.candidateVendors[0]?.vendorName ?? '';
    default:
      return '';
  }
}

const MATCH_TYPE_OPTIONS = [
  { value: 'Domain', label: 'Domain (e.g. acme.com)' },
  { value: 'ExactAddress', label: 'Exact address (e.g. ap@acme.com)' },
];

const COMPANY_MATCH_MODE_OPTIONS = [
  { value: 'Enforce', label: 'Hold it — an invoice billed to another company waits for a person' },
  { value: 'WarnOnly', label: 'Warn only — flag it on the document, but let it through' },
  { value: 'Off', label: 'Off — do not check who the invoice is addressed to' },
];

const TIER_OPTIONS = [
  { value: 'EmailIntake', label: 'Tier 1 — forwarding (shared mailbox, no sign-in)' },
  { value: 'ConnectedMailbox', label: 'Tier 2 — connect your own mailbox (Microsoft sign-in)' },
];

/**
 * Setup, status and approved-rule administration for email intake — both tiers the bot supports.
 *
 * Tier 1 (forwarding): activating gets a bot-assigned inbound address back; a tenant forwards or
 * CCs vendor invoice emails to it. Tier 2 (connected mailbox): activating gets a Microsoft
 * consent URL back instead — opening it and granting access is what actually connects the
 * tenant's own mailbox, at which point the bot monitors it directly. Either way, the SAME
 * extraction/review/posting pipeline every other intake channel uses picks the invoice up from
 * there; this page only configures the mailbox side and the sender-to-vendor rules that let the
 * bot route a matched email straight to the right vendor.
 *
 * There is no push signal for "OAuth just completed" — the bot's own callback page does not
 * redirect back into this app. Status is read fresh on load and on demand (Refresh status); a
 * person completing consent in another tab comes back here and clicks it themselves.
 */
function EmailIntakeContent(): JSX.Element {
  const toast = useToast();
  const state = useAsync((signal) => emailIntake.status(signal), []);
  const rulesState = useAsync((signal) => emailIntake.listRules(signal), []);
  const forwardersState = useAsync((signal) => emailIntake.listForwarders(signal), []);
  const forwarderTable = useSortableTable<ForwarderColumn>({
    storageKey: 'emailIntakeForwardersColumnWidths',
    columns: { address: { minWidth: 180 }, label: { minWidth: 140 }, added: { minWidth: 130 } },
  });
  const ruleTable = useSortableTable<ApprovedRuleColumn>({
    storageKey: 'emailIntakeRulesColumnWidths',
    columns: {
      match: { minWidth: 110 },
      value: { minWidth: 160 },
      vendor: { minWidth: 160 },
      added: { minWidth: 130 },
    },
  });
  const pendingState = useAsync((signal) => emailIntake.listPendingSenders(signal), []);
  const heldState = useAsync((signal) => emailIntake.listHeldInvoices(signal), []);
  const aliasState = useAsync((signal) => emailIntake.listCompanyAliases(signal), []);
  const heldTable = useSortableTable<HeldInvoiceColumn>({
    storageKey: 'emailIntakeHeldColumnWidths',
    columns: {
      invoice: { minWidth: 160 },
      vendor: { minWidth: 160 },
      addressedTo: { minWidth: 180 },
      amount: { minWidth: 100 },
      received: { minWidth: 130 },
    },
  });
  const aliasTable = useSortableTable<AliasColumn>({
    storageKey: 'emailIntakeAliasesColumnWidths',
    columns: {
      company: { minWidth: 160 },
      address: { minWidth: 180 },
      note: { minWidth: 160 },
      added: { minWidth: 130 },
    },
  });
  const pendingTable = useSortableTable<PendingSenderColumn>({
    storageKey: 'emailIntakePendingColumnWidths',
    columns: {
      from: { minWidth: 180 },
      domain: { minWidth: 150 },
      subject: { minWidth: 180 },
      received: { minWidth: 130 },
      vendor: { minWidth: 150 },
    },
  });

  const [intakeTier, setIntakeTier] = useState<'EmailIntake' | 'ConnectedMailbox'>('EmailIntake');
  const [apMailboxAddress, setApMailboxAddress] = useState('');
  const [adminNotificationEmail, setAdminNotificationEmail] = useState('');
  const [requireReview, setRequireReview] = useState(true);
  const [minConfidence, setMinConfidence] = useState(65);
  // The API stores this as ProcessAttachmentsOnly (attachment-only, the safe default); the
  // checkbox is framed the other way round, as the thing an administrator would actually want.
  const [readInvoiceFromBody, setReadInvoiceFromBody] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const [newForwarderAddress, setNewForwarderAddress] = useState('');
  const [newForwarderLabel, setNewForwarderLabel] = useState('');
  const [forwarderBusy, setForwarderBusy] = useState<string | null>(null);

  const [newMatchType, setNewMatchType] = useState<'Domain' | 'ExactAddress'>('Domain');
  const [newMatchValue, setNewMatchValue] = useState('');
  const [newVendorId, setNewVendorId] = useState('');
  const [newVendorName, setNewVendorName] = useState('');
  const [ruleBusy, setRuleBusy] = useState<string | null>(null);

  // Per pending-sender row: which candidate vendor is selected (or 'other' to type a new one).
  const [pendingVendorChoice, setPendingVendorChoice] = useState<Record<string, string>>({});
  const [pendingOtherVendorId, setPendingOtherVendorId] = useState<Record<string, string>>({});
  const [pendingOtherVendorName, setPendingOtherVendorName] = useState<Record<string, string>>({});
  const [pendingBusy, setPendingBusy] = useState<string | null>(null);

  const [heldBusy, setHeldBusy] = useState<string | null>(null);
  const [aliasBusy, setAliasBusy] = useState<string | null>(null);
  const [newAliasName, setNewAliasName] = useState('');
  const [newAliasAddress, setNewAliasAddress] = useState('');
  const [newAliasNote, setNewAliasNote] = useState('');

  useEffect(() => {
    if (state.data) {
      setIntakeTier(state.data.intakeTier === 'ConnectedMailbox' ? 'ConnectedMailbox' : 'EmailIntake');
      setAdminNotificationEmail(state.data.adminNotificationEmail ?? '');
      // Show what is actually stored. This used to be pinned to true and the slider left at
      // whatever it happened to be, which was survivable while neither value did anything on this
      // side. Now that review is enforced here, a screen saying "on" over a connection that is off
      // would be telling an administrator the opposite of what the rule engine will do.
      setRequireReview(state.data.requireReviewBeforeApply);
      setMinConfidence(state.data.minimumConfidencePercent);
      setReadInvoiceFromBody(!state.data.processAttachmentsOnly);
    }
  }, [state.data]);

  const run = async <T,>(name: string, action: () => Promise<T>, onSuccess: (result: T) => void) => {
    setBusy(name);
    try {
      const result = await action();
      onSuccess(result);
    } catch (caught) {
      toast.error(`${name} did not complete`, errorMessage(caught));
    } finally {
      setBusy(null);
    }
  };

  const activate = () => {
    void run(
      'Activate',
      () =>
        emailIntake.activate({
          apMailboxAddress: apMailboxAddress.trim() || null,
          requireReviewBeforeApply: requireReview,
          minimumConfidencePercent: minConfidence,
          intakeTier,
          adminNotificationEmail: adminNotificationEmail.trim() || null,
          processAttachmentsOnly: !readInvoiceFromBody,
        }),
      (updated) => {
        state.setData(updated);
        if (updated.status === 'Error') {
          toast.error('Email intake could not be activated', updated.lastErrorMessage ?? 'The bot refused the setup call.');
        } else if (updated.status === 'ConsentRequired') {
          toast.success('Almost there', 'Open the Microsoft sign-in link below to connect your mailbox.');
        } else if (updated.intakeTier === 'ConnectedMailbox') {
          toast.success('Email intake activated', updated.connectedMailboxAddress
            ? `Monitoring ${updated.connectedMailboxAddress}.`
            : 'Setup saved.');
        } else {
          toast.success('Email intake activated', updated.inboundEmailAddress
            ? `Forward vendor invoices to ${updated.inboundEmailAddress}.`
            : 'Setup saved.');
        }
      },
    );
  };

  const refreshStatus = () => void run('Refresh', () => emailIntake.status(), (updated) => state.setData(updated));

  const copyForwardAddress = (address: string) => {
    navigator.clipboard
      .writeText(address)
      .then(() => toast.success('Address copied', 'Share it with whoever handles AP.'))
      .catch(() => toast.error('Could not copy', 'Select and copy the address manually.'));
  };

  const disable = () => {
    void run(
      'Turn off',
      () => emailIntake.disable({ reason: null }),
      (updated) => {
        state.setData(updated);
        toast.success('Email intake turned off');
      },
    );
  };

  const runNow = () => {
    void run(
      'Check now',
      () => emailIntake.runNow(),
      (outcome) => {
        if (outcome.success) {
          const imported = outcome.imported ?? 0;
          toast.success(
            'Checked the mailbox',
            imported > 0
              ? `${imported} new email invoice(s) imported.`
              : outcome.itemsSeen
                ? `${outcome.itemsSeen} item(s) seen, nothing new.`
                : 'Nothing new since the last check.',
          );
        } else {
          toast.error('Could not check the mailbox', outcome.message ?? 'Refused.');
        }
        state.reload();
      },
    );
  };

  const addForwarder = () => {
    if (!newForwarderAddress.trim()) {
      toast.error('An email address is required');
      return;
    }
    setForwarderBusy('add');
    emailIntake
      .upsertForwarder({ emailAddress: newForwarderAddress.trim(), label: newForwarderLabel.trim() || null })
      .then(() => {
        toast.success('Forwarding address registered');
        setNewForwarderAddress('');
        setNewForwarderLabel('');
        forwardersState.reload();
      })
      .catch((caught) => toast.error('Could not register the address', errorMessage(caught)))
      .finally(() => setForwarderBusy(null));
  };

  const deleteForwarder = (forwarder: EmailIntakeForwarderDto) => {
    setForwarderBusy(forwarder.id);
    emailIntake
      .deleteForwarder(forwarder.id)
      .then(() => {
        toast.success('Forwarding address removed');
        forwardersState.reload();
      })
      .catch((caught) => toast.error('Could not remove the address', errorMessage(caught)))
      .finally(() => setForwarderBusy(null));
  };

  const addRule = () => {
    if (!newMatchValue.trim() || !newVendorId.trim()) {
      toast.error('A match value and a QuickBooks vendor id are required');
      return;
    }
    setRuleBusy('add');
    emailIntake
      .upsertRule({
        matchType: newMatchType,
        matchValue: newMatchValue.trim(),
        qboVendorId: newVendorId.trim(),
        vendorName: newVendorName.trim(),
      })
      .then(() => {
        toast.success('Rule saved');
        setNewMatchValue('');
        setNewVendorId('');
        setNewVendorName('');
        rulesState.reload();
      })
      .catch((caught) => toast.error('Could not save rule', errorMessage(caught)))
      .finally(() => setRuleBusy(null));
  };

  const deleteRule = (rule: EmailIntakeApprovedRuleDto) => {
    setRuleBusy(rule.id);
    emailIntake
      .deleteRule(rule.id)
      .then(() => {
        toast.success('Rule removed');
        rulesState.reload();
      })
      .catch((caught) => toast.error('Could not remove rule', errorMessage(caught)))
      .finally(() => setRuleBusy(null));
  };

  const ignorePendingSender = (item: EmailIntakePendingSenderDto) => {
    setPendingBusy(item.pendingSenderId);
    emailIntake
      .decidePendingSender(item.pendingSenderId, { decision: 'Ignore', qboVendorId: null, vendorName: null })
      .then(() => {
        toast.success('Sender ignored');
        pendingState.reload();
        state.reload();
      })
      .catch((caught) => toast.error('Could not ignore sender', errorMessage(caught)))
      .finally(() => setPendingBusy(null));
  };

  const addPendingSender = (item: EmailIntakePendingSenderDto) => {
    const choice = pendingVendorChoice[item.pendingSenderId];
    const isOther = choice === 'other' || !choice;
    const qboVendorId = isOther
      ? (pendingOtherVendorId[item.pendingSenderId] ?? '').trim()
      : choice;
    const vendorName = isOther
      ? (pendingOtherVendorName[item.pendingSenderId] ?? '').trim()
      : (item.candidateVendors.find((v) => v.qboVendorId === choice)?.vendorName ?? '');

    if (!qboVendorId) {
      toast.error('Pick a vendor, or enter a QuickBooks vendor id, before adding this sender');
      return;
    }

    setPendingBusy(item.pendingSenderId);
    emailIntake
      .decidePendingSender(item.pendingSenderId, { decision: 'Add', qboVendorId, vendorName: vendorName || null })
      .then(() => {
        toast.success('Sender added', `Future messages from ${item.fromAddress} will route to ${vendorName || qboVendorId} automatically.`);
        pendingState.reload();
        rulesState.reload();
        state.reload();
      })
      .catch((caught) => toast.error('Could not add sender', errorMessage(caught)))
      .finally(() => setPendingBusy(null));
  };

  const resolveHeld = (item: EmailIntakeHeldInvoiceDto, decision: 'Accept' | 'Reject') => {
    setHeldBusy(item.documentId);
    emailIntake
      .resolveHeldInvoice(item.documentId, { decision })
      .then(() => {
        toast.success(
          decision === 'Accept' ? 'Invoice released for review' : 'Invoice rejected',
          decision === 'Accept'
            ? 'It is back in the normal review queue. Nothing has posted yet.'
            : `Ask ${item.vendorName ?? 'the vendor'} to correct the billing address and resend it.`,
        );
        heldState.reload();
        state.reload();
      })
      .catch((caught) => toast.error('Could not resolve this invoice', errorMessage(caught)))
      .finally(() => setHeldBusy(null));
  };

  const setMatchMode = (mode: string) => {
    setBusy('MatchMode');
    emailIntake
      .setCompanyMatchMode({ mode: mode as 'Enforce' | 'WarnOnly' | 'Off' })
      .then(() => {
        toast.success(
          'Bill-to check updated',
          mode === 'Off'
            ? 'Invoices are no longer checked against who they are addressed to.'
            : mode === 'WarnOnly'
              ? 'Mismatches will be flagged on the document but will not hold it.'
              : 'An invoice billed to another company will be held for a person.',
        );
        state.reload();
        heldState.reload();
      })
      .catch((caught) => toast.error('Could not change the bill-to check', errorMessage(caught)))
      .finally(() => setBusy(null));
  };

  const addAlias = () => {
    if (!newAliasName.trim() && !newAliasAddress.trim()) {
      toast.error('Give a company name, an address fragment, or both');
      return;
    }

    setAliasBusy('add');
    emailIntake
      .addCompanyAlias({
        name: newAliasName.trim() || null,
        addressContains: newAliasAddress.trim() || null,
        note: newAliasNote.trim() || null,
      })
      .then(() => {
        toast.success('Accepted alternate added', 'Invoices billed this way will no longer be held.');
        setNewAliasName('');
        setNewAliasAddress('');
        setNewAliasNote('');
        aliasState.reload();
        state.reload();
      })
      .catch((caught) => toast.error('Could not add the alternate', errorMessage(caught)))
      .finally(() => setAliasBusy(null));
  };

  const deleteAlias = (alias: EmailIntakeCompanyAliasDto) => {
    setAliasBusy(alias.id);
    emailIntake
      .deleteCompanyAlias(alias.id)
      .then(() => {
        toast.success('Alternate removed');
        aliasState.reload();
        state.reload();
      })
      .catch((caught) => toast.error('Could not remove the alternate', errorMessage(caught)))
      .finally(() => setAliasBusy(null));
  };

  const refreshCompanyIdentity = () => {
    setBusy('CompanyIdentity');
    emailIntake
      .refreshCompanyIdentity()
      .then((result) => {
        if (result.success) toast.success('Company details refreshed', result.message);
        else toast.error('Could not refresh company details', result.message);
        state.reload();
      })
      .catch((caught) => toast.error('Could not refresh company details', errorMessage(caught)))
      .finally(() => setBusy(null));
  };

  if (state.loading && !state.data) {
    return (
      <>
        <PageHeader title="Email Intake" />
        <CardSkeleton />
      </>
    );
  }

  if (state.error && !state.data) {
    return (
      <>
        <PageHeader title="Email Intake" />
        <ErrorState message="Email intake status could not be loaded." detail={state.error} onRetry={state.reload} />
      </>
    );
  }

  const d: EmailIntakeStatusDto | null = state.data;
  if (!d) return <EmptyState title="No email intake status returned" />;

  return (
    <>
      <PageHeader
        title="Email Intake"
        description="Let vendor invoices arrive by email instead of an upload — forward them to a shared address (Tier 1), or connect your own mailbox (Tier 2)."
      />

      {d.status === 'ConsentRequired' ? (
        <div className="banner banner-info" role="status">
          <div className="banner-body">
            <p className="banner-title">Almost there — connect your mailbox</p>
            <p className="banner-detail">
              {d.consentUrl ? (
                <>
                  Open{' '}
                  <a href={d.consentUrl} target="_blank" rel="noreferrer noopener">
                    this Microsoft sign-in link
                  </a>{' '}
                  and grant access. Come back and click <strong>Refresh status</strong> once you have — this
                  page has no way to know the moment it finishes.
                </>
              ) : (
                'Waiting on a fresh sign-in link from the bot — click Refresh status below.'
              )}
            </p>
          </div>
        </div>
      ) : d.enabled ? (
        <div className={`banner ${d.status === 'Error' ? 'banner-warn' : 'banner-good'}`} role="status">
          <div className="banner-body">
            <p className="banner-title">
              {d.status === 'ReadyForForwarding' || d.status === 'Connected' ? 'Email intake is on' : d.status}
            </p>
            {d.intakeTier === 'ConnectedMailbox' ? (
              d.connectedMailboxAddress ? (
                <>
                  <p className="banner-detail">
                    Monitoring <strong>{d.connectedMailboxAddress}</strong> directly. Attachments are classified
                    and, when matched to an approved rule or read confidently enough, turned into a vendor
                    invoice here automatically.
                  </p>
                  <p className="banner-detail">
                    We do not retain your forwarded emails. Once an attachment is processed, the original
                    message is deleted from this mailbox — typically within minutes. A message from a sender
                    not yet approved as a vendor is held securely for review for up to 7 days, then deleted
                    either way.
                  </p>
                </>
              ) : (
                <p className="banner-detail">Connected, but the bot has not reported a mailbox address yet.</p>
              )
            ) : d.inboundEmailAddress ? (
              <>
                <p className="banner-detail">
                  Forward or CC vendor invoice emails to <strong>{d.inboundEmailAddress}</strong>{' '}
                  <button
                    type="button"
                    className="btn btn-link"
                    onClick={() => copyForwardAddress(d.inboundEmailAddress!)}
                  >
                    Copy
                  </button>
                  . Attachments are classified and, when matched to an approved rule or read confidently enough,
                  turned into a vendor invoice here automatically.
                </p>
                <p className="banner-detail">
                  We do not retain your forwarded emails. Once an attachment is processed, the original
                  message is deleted from this mailbox — typically within minutes. A message from a sender
                  not yet approved as a vendor is held securely for review for up to 7 days, then deleted
                  either way.
                </p>
              </>
            ) : (
              <p className="banner-detail">Enabled, but the bot has not returned an inbound address yet.</p>
            )}
            {d.lastErrorMessage ? <p className="banner-detail">{d.lastErrorMessage}</p> : null}
          </div>
        </div>
      ) : (
        <div className="banner banner-info" role="status">
          <div className="banner-body">
            <p className="banner-title">Email intake is off</p>
            <p className="banner-detail">
              Choose a tier and activate below — Tier 1 gets you a mailbox address to forward invoices to; Tier
              2 connects your own mailbox instead, once you grant Microsoft access.
            </p>
          </div>
        </div>
      )}

      {d.entitlementMessage && !d.tier1Allowed && !d.tier2Allowed ? (
        <div className="banner banner-warn" role="alert">
          <div className="banner-body">
            <p className="banner-title">Neither tier is available for this package</p>
            <p className="banner-detail">{d.entitlementMessage}</p>
          </div>
        </div>
      ) : null}

      <div className="grid-2">
        <Panel title="Setup" bodyClassName="form-grid">
          <SelectField
            label="Tier"
            value={intakeTier}
            onChange={(value) => setIntakeTier(value as 'EmailIntake' | 'ConnectedMailbox')}
            options={TIER_OPTIONS}
            hint={
              d.tier1Allowed && d.tier2Allowed
                ? 'Both tiers are available for this package — pick whichever suits how invoices arrive.'
                : undefined
            }
          />
          <TextField
            label="Your AP mailbox address"
            value={apMailboxAddress}
            onChange={setApMailboxAddress}
            hint="Optional — just a label the bot uses to describe this setup back to you."
          />
          <TextField
            label="Admin notification email"
            value={adminNotificationEmail}
            onChange={setAdminNotificationEmail}
            placeholder="ap-admin@yourcompany.com"
            hint="Where a message from a known domain but an unconfirmed sender gets forwarded for review. Leave blank and it still shows up below — it just won't be emailed anywhere."
          />
          <CheckboxField
            label="Require review before posting"
            checked={requireReview}
            onChange={setRequireReview}
            hint="When on, an emailed invoice always goes to the review queue, even if a sync rule says auto-post. Turn it off only if you are content for email invoices to post with nobody looking at them."
          />
          <SliderField
            label="Minimum classification confidence"
            value={minConfidence}
            onChange={setMinConfidence}
            hint="An email the bot is less sure is an invoice, below this score, is not imported."
          />
          <CheckboxField
            label="Also read an invoice typed directly into the email"
            checked={readInvoiceFromBody}
            onChange={setReadInvoiceFromBody}
            hint="When on, a message with no supported attachment is still classified against its own body text instead of being ignored — for an invoice with no PDF at all."
          />
          <Toolbar>
            {d.enabled ? (
              <button type="button" className="btn btn-secondary" disabled={busy !== null} onClick={disable}>
                {busy === 'Turn off' ? 'Turning off…' : 'Turn off'}
              </button>
            ) : null}
            <button type="button" className="btn btn-primary" disabled={busy !== null} onClick={activate}>
              {busy === 'Activate' ? 'Activating…' : d.enabled ? 'Save setup' : 'Activate'}
            </button>
          </Toolbar>
        </Panel>

        <Panel title="Status">
          <KeyValueList columns={1}>
            <div className="kv">
              <dt className="kv-label">Tier</dt>
              <dd className="kv-value">
                {d.intakeTier === 'ConnectedMailbox' ? 'Tier 2 — connected mailbox' : 'Tier 1 — forwarding'}
              </dd>
            </div>
            {d.intakeTier === 'ConnectedMailbox' ? (
              <div className="kv">
                <dt className="kv-label">Connected mailbox</dt>
                <dd className="kv-value">
                  {d.connectedMailboxAddress ?? '—'}
                  {d.lastConnectedUtc ? <span className="cell-sub">since {formatRelative(d.lastConnectedUtc)}</span> : null}
                </dd>
              </div>
            ) : null}
            <div className="kv">
              <dt className="kv-label">Approved rules</dt>
              <dd className="kv-value">{d.approvedRuleCount}</dd>
            </div>
            <div className="kv">
              <dt className="kv-label">Pending senders</dt>
              <dd className="kv-value">{d.pendingSenderCount}</dd>
            </div>
            <div className="kv">
              <dt className="kv-label">Last checked</dt>
              <dd className="kv-value">{d.lastMonitorUtc ? formatRelative(d.lastMonitorUtc) : 'never'}</dd>
            </div>
            <div className="kv">
              <dt className="kv-label">Last import</dt>
              <dd className="kv-value">
                {d.lastImportUtc ? formatRelative(d.lastImportUtc) : 'never'}
                {d.lastImportMessage ? <span className="cell-sub">{d.lastImportMessage}</span> : null}
              </dd>
            </div>
          </KeyValueList>
          <Toolbar>
            <button type="button" className="btn btn-primary" disabled={busy !== null || !d.enabled} onClick={runNow}>
              {busy === 'Check now' ? 'Checking…' : 'Check now'}
            </button>
            <button type="button" className="btn btn-secondary" disabled={busy !== null} onClick={refreshStatus}>
              {busy === 'Refresh' ? 'Refreshing…' : 'Refresh status'}
            </button>
          </Toolbar>
          <p className="field-hint">
            Runs automatically in the background on its own schedule; Check now is a manual trigger for the
            same pass. Refresh status just re-reads where things stand — useful right after completing
            Microsoft sign-in for a Tier 2 mailbox.
          </p>
        </Panel>
      </div>

      <Panel
        title="Registered forwarding addresses"
        description="The only addresses allowed to submit into this Tier 1 mailbox at all — your own AP staff mailbox(es), not vendors. A message from anyone else is rejected outright, even if it would otherwise match an approved vendor rule below."
      >
        {forwardersState.error ? <InlineError message={forwardersState.error} /> : null}
        <TableWrap busy={forwardersState.loading} tableClassName="data-table-sortable">
          <colgroup>
            <col style={forwarderTable.columnStyle('address')} />
            <col style={forwarderTable.columnStyle('label')} />
            <col style={forwarderTable.columnStyle('added')} />
            <col />
          </colgroup>
          <thead>
            <tr>
              <SortableTh
                label="Email address"
                sortKey="address"
                currentSortKey={forwarderTable.sortKey}
                sortDir={forwarderTable.sortDir}
                onSort={forwarderTable.toggleSort}
                style={forwarderTable.columnStyle('address')}
                onResizeStart={forwarderTable.beginResize('address')}
              />
              <SortableTh
                label="Label"
                sortKey="label"
                currentSortKey={forwarderTable.sortKey}
                sortDir={forwarderTable.sortDir}
                onSort={forwarderTable.toggleSort}
                style={forwarderTable.columnStyle('label')}
                onResizeStart={forwarderTable.beginResize('label')}
              />
              <SortableTh
                label="Added"
                sortKey="added"
                currentSortKey={forwarderTable.sortKey}
                sortDir={forwarderTable.sortDir}
                onSort={forwarderTable.toggleSort}
                style={forwarderTable.columnStyle('added')}
                onResizeStart={forwarderTable.beginResize('added')}
              />
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {forwarderTable.sort(forwardersState.data ?? [], forwarderSortValue).map((forwarder) => (
              <tr key={forwarder.id}>
                <td>{forwarder.emailAddress}</td>
                <td>{forwarder.label || '—'}</td>
                <td>{formatDateTime(forwarder.createdUtc)}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-link btn-danger"
                    disabled={forwarderBusy !== null}
                    onClick={() => deleteForwarder(forwarder)}
                  >
                    {forwarderBusy === forwarder.id ? 'Removing…' : 'Remove'}
                  </button>
                </td>
              </tr>
            ))}
            <tr>
              <td>
                <TextField label="" value={newForwarderAddress} onChange={setNewForwarderAddress} placeholder="ap@yourcompany.com" />
              </td>
              <td>
                <TextField label="" value={newForwarderLabel} onChange={setNewForwarderLabel} placeholder="Label (optional)" />
              </td>
              <td />
              <td>
                <button type="button" className="btn btn-secondary" disabled={forwarderBusy !== null} onClick={addForwarder}>
                  {forwarderBusy === 'add' ? 'Adding…' : 'Add'}
                </button>
              </td>
            </tr>
          </tbody>
        </TableWrap>
        {!forwardersState.loading && (forwardersState.data?.length ?? 0) === 0 ? (
          <p className="field-hint">
            No forwarding addresses registered yet — until one is added, the bot rejects every message sent to
            the Tier 1 address, including from an already-approved vendor.
          </p>
        ) : null}
      </Panel>

      <Panel
        title="Approved sender rules"
        description="A sender domain or exact address bound to a vendor routes straight to that vendor without waiting on extraction to guess."
      >
        {rulesState.error ? <InlineError message={rulesState.error} /> : null}
        <TableWrap busy={rulesState.loading} tableClassName="data-table-sortable">
          <colgroup>
            <col style={ruleTable.columnStyle('match')} />
            <col style={ruleTable.columnStyle('value')} />
            <col style={ruleTable.columnStyle('vendor')} />
            <col style={ruleTable.columnStyle('added')} />
            <col />
          </colgroup>
          <thead>
            <tr>
              <SortableTh
                label="Match"
                sortKey="match"
                currentSortKey={ruleTable.sortKey}
                sortDir={ruleTable.sortDir}
                onSort={ruleTable.toggleSort}
                style={ruleTable.columnStyle('match')}
                onResizeStart={ruleTable.beginResize('match')}
              />
              <SortableTh
                label="Value"
                sortKey="value"
                currentSortKey={ruleTable.sortKey}
                sortDir={ruleTable.sortDir}
                onSort={ruleTable.toggleSort}
                style={ruleTable.columnStyle('value')}
                onResizeStart={ruleTable.beginResize('value')}
              />
              <SortableTh
                label="QuickBooks vendor"
                sortKey="vendor"
                currentSortKey={ruleTable.sortKey}
                sortDir={ruleTable.sortDir}
                onSort={ruleTable.toggleSort}
                style={ruleTable.columnStyle('vendor')}
                onResizeStart={ruleTable.beginResize('vendor')}
              />
              <SortableTh
                label="Added"
                sortKey="added"
                currentSortKey={ruleTable.sortKey}
                sortDir={ruleTable.sortDir}
                onSort={ruleTable.toggleSort}
                style={ruleTable.columnStyle('added')}
                onResizeStart={ruleTable.beginResize('added')}
              />
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {ruleTable.sort(rulesState.data ?? [], approvedRuleSortValue).map((rule) => (
              <tr key={rule.id}>
                <td>{rule.matchType === 'Domain' ? 'Domain' : 'Exact address'}</td>
                <td>{rule.matchValue}</td>
                <td>
                  {rule.vendorName || rule.qboVendorId}
                  {rule.vendorName ? <span className="cell-sub">{rule.qboVendorId}</span> : null}
                </td>
                <td>{formatDateTime(rule.createdUtc)}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-link btn-danger"
                    disabled={ruleBusy !== null}
                    onClick={() => deleteRule(rule)}
                  >
                    {ruleBusy === rule.id ? 'Removing…' : 'Remove'}
                  </button>
                </td>
              </tr>
            ))}
            <tr>
              <td>
                <SelectField
                  label=""
                  value={newMatchType}
                  onChange={(value) => setNewMatchType(value as 'Domain' | 'ExactAddress')}
                  options={MATCH_TYPE_OPTIONS}
                />
              </td>
              <td>
                <TextField
                  label=""
                  value={newMatchValue}
                  onChange={setNewMatchValue}
                  placeholder={newMatchType === 'Domain' ? 'acme.com' : 'ap@acme.com'}
                />
              </td>
              <td>
                <TextField label="" value={newVendorId} onChange={setNewVendorId} placeholder="QuickBooks vendor id" />
                <TextField label="" value={newVendorName} onChange={setNewVendorName} placeholder="Vendor name (optional)" />
              </td>
              <td />
              <td>
                <button type="button" className="btn btn-secondary" disabled={ruleBusy !== null} onClick={addRule}>
                  {ruleBusy === 'add' ? 'Adding…' : 'Add'}
                </button>
              </td>
            </tr>
          </tbody>
        </TableWrap>
        {!rulesState.loading && (rulesState.data?.length ?? 0) === 0 ? (
          <p className="field-hint">
            No rules yet, so nothing is processed: a sender at an unregistered domain is skipped without being
            read at all, and a sender at a registered domain still waits for a person to approve it below —
            an exact address is what actually lets a message auto-route.
          </p>
        ) : null}
      </Panel>

      {/*
        THE ONLY PLACE A MIS-ADDRESSED INVOICE BECOMES VISIBLE.

        Deliberately above Pending senders: that queue is about whether a SENDER is known, which is
        housekeeping. This one is about an invoice that is already in the building and belongs to
        somebody else, and it is the one that costs real money if nobody looks.
      */}
      <Panel
        title="Held — addressed to another company"
        description="The invoice itself names a different company in the Bill To block, so it has not been booked. The address an email arrived at does not decide whose invoice it is; the document does."
      >
        {heldState.error ? <InlineError message={heldState.error} /> : null}

        {!d.companyIdentityKnown ? (
          <div className="banner banner-warn" role="status">
            <div className="banner-body">
            <p>
              <strong>This check is not running.</strong> iERP AI does not have this company&rsquo;s own name
              and address, so nothing is comparing incoming invoices against who you are — an invoice
              addressed to another company would be taken in and booked here without comment.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy !== null}
              onClick={refreshCompanyIdentity}
            >
              {busy === 'CompanyIdentity' ? 'Reading QuickBooks…' : 'Read company details from QuickBooks'}
            </button>
            </div>
          </div>
        ) : (
          <p className="field-hint">
            Checking every incoming invoice against <strong>{d.companyIdentity}</strong>.{' '}
            <button type="button" className="btn btn-link" disabled={busy !== null} onClick={refreshCompanyIdentity}>
              {busy === 'CompanyIdentity' ? 'Refreshing…' : 'Refresh from QuickBooks'}
            </button>
          </p>
        )}

        <SelectField
          label="When an invoice is billed to another company"
          value={d.companyMatchMode}
          onChange={setMatchMode}
          options={COMPANY_MATCH_MODE_OPTIONS}
          disabled={busy !== null}
          hint="Off is for testing against companies whose invoices carry the wrong details anyway. Warn only keeps the finding visible without stopping the work."
        />

        {d.companyMatchMode !== 'Enforce' ? (
          <div className="banner banner-warn" role="status">
            <div className="banner-body">
              <p>
                {d.companyMatchMode === 'Off'
                  ? 'Nothing is checking who invoices are addressed to. An invoice for another company would be taken in and booked here.'
                  : 'Mismatches are being flagged but not held, so an invoice for another company can still be posted by whoever reviews it.'}
              </p>
            </div>
          </div>
        ) : null}

        <TableWrap busy={heldState.loading} tableClassName="data-table-sortable">
          <colgroup>
            <col style={heldTable.columnStyle('invoice')} />
            <col style={heldTable.columnStyle('vendor')} />
            <col style={heldTable.columnStyle('addressedTo')} />
            <col style={heldTable.columnStyle('amount')} />
            <col style={heldTable.columnStyle('received')} />
            <col />
          </colgroup>
          <thead>
            <tr>
              <SortableTh
                label="Invoice"
                sortKey="invoice"
                currentSortKey={heldTable.sortKey}
                sortDir={heldTable.sortDir}
                onSort={heldTable.toggleSort}
                style={heldTable.columnStyle('invoice')}
                onResizeStart={heldTable.beginResize('invoice')}
              />
              <SortableTh
                label="Vendor"
                sortKey="vendor"
                currentSortKey={heldTable.sortKey}
                sortDir={heldTable.sortDir}
                onSort={heldTable.toggleSort}
                style={heldTable.columnStyle('vendor')}
                onResizeStart={heldTable.beginResize('vendor')}
              />
              <SortableTh
                label="Addressed to"
                sortKey="addressedTo"
                currentSortKey={heldTable.sortKey}
                sortDir={heldTable.sortDir}
                onSort={heldTable.toggleSort}
                style={heldTable.columnStyle('addressedTo')}
                onResizeStart={heldTable.beginResize('addressedTo')}
              />
              <SortableTh
                label="Amount"
                sortKey="amount"
                currentSortKey={heldTable.sortKey}
                sortDir={heldTable.sortDir}
                onSort={heldTable.toggleSort}
                style={heldTable.columnStyle('amount')}
                onResizeStart={heldTable.beginResize('amount')}
              />
              <SortableTh
                label="Received"
                sortKey="received"
                currentSortKey={heldTable.sortKey}
                sortDir={heldTable.sortDir}
                onSort={heldTable.toggleSort}
                style={heldTable.columnStyle('received')}
                onResizeStart={heldTable.beginResize('received')}
              />
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {heldTable.sort(heldState.data ?? [], heldInvoiceSortValue).map((item) => (
              <tr key={item.documentId}>
                <td>
                  {item.invoiceNumber || item.fileName}
                  {item.invoiceNumber ? <span className="cell-sub">{item.fileName}</span> : null}
                </td>
                <td>
                  {item.vendorName || '—'}
                  {item.fromAddress ? <span className="cell-sub">{item.fromAddress}</span> : null}
                </td>
                <td>
                  <strong>{item.billToName || 'Not stated'}</strong>
                  {item.billToAddress ? <span className="cell-sub">{item.billToAddress}</span> : null}
                  {item.suggestedCompany ? (
                    <span className="cell-sub">Looks like {item.suggestedCompany}</span>
                  ) : null}
                  {item.thisCompany ? <span className="cell-sub">You are {item.thisCompany}</span> : null}
                </td>
                <td>
                  {item.invoiceTotal !== null
                    ? `${item.invoiceTotal.toFixed(2)} ${item.currencyCode ?? ''}`.trim()
                    : '—'}
                </td>
                <td>{item.receivedUtc ? formatDateTime(item.receivedUtc) : '—'}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={heldBusy !== null}
                    onClick={() => resolveHeld(item, 'Accept')}
                    title="This invoice does belong to this company despite what it prints. Releases it into the normal review queue — it does not post it."
                  >
                    {heldBusy === item.documentId ? 'Working…' : 'It is ours'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-link btn-danger"
                    disabled={heldBusy !== null}
                    onClick={() => resolveHeld(item, 'Reject')}
                    title="Not this company's invoice. Closes the document; ask the vendor to correct the billing address."
                  >
                    Not ours
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </TableWrap>

        {!heldState.loading && (heldState.data?.length ?? 0) === 0 ? (
          <p className="field-hint">
            {d.companyIdentityKnown
              ? 'Nothing is held. Every invoice taken in so far was addressed to this company.'
              : 'Nothing is held — but nothing is being checked either, so that is not the same as nothing being wrong.'}
          </p>
        ) : null}
      </Panel>

      <Panel
        title="Also accept these billing details"
        description="Billing details that are still this company even though QuickBooks no longer says so — usually an address a vendor has not got round to updating. An invoice matching one of these is let through and marked as having used an alternate, so you can see who is still behind."
      >
        {aliasState.error ? <InlineError message={aliasState.error} /> : null}
        <TableWrap busy={aliasState.loading} tableClassName="data-table-sortable">
          <colgroup>
            <col style={aliasTable.columnStyle('company')} />
            <col style={aliasTable.columnStyle('address')} />
            <col style={aliasTable.columnStyle('note')} />
            <col style={aliasTable.columnStyle('added')} />
            <col />
          </colgroup>
          <thead>
            <tr>
              <SortableTh
                label="Company name"
                sortKey="company"
                currentSortKey={aliasTable.sortKey}
                sortDir={aliasTable.sortDir}
                onSort={aliasTable.toggleSort}
                style={aliasTable.columnStyle('company')}
                onResizeStart={aliasTable.beginResize('company')}
              />
              <SortableTh
                label="Address contains"
                sortKey="address"
                currentSortKey={aliasTable.sortKey}
                sortDir={aliasTable.sortDir}
                onSort={aliasTable.toggleSort}
                style={aliasTable.columnStyle('address')}
                onResizeStart={aliasTable.beginResize('address')}
              />
              <SortableTh
                label="Note"
                sortKey="note"
                currentSortKey={aliasTable.sortKey}
                sortDir={aliasTable.sortDir}
                onSort={aliasTable.toggleSort}
                style={aliasTable.columnStyle('note')}
                onResizeStart={aliasTable.beginResize('note')}
              />
              <SortableTh
                label="Added"
                sortKey="added"
                currentSortKey={aliasTable.sortKey}
                sortDir={aliasTable.sortDir}
                onSort={aliasTable.toggleSort}
                style={aliasTable.columnStyle('added')}
                onResizeStart={aliasTable.beginResize('added')}
              />
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {aliasTable.sort(aliasState.data ?? [], aliasSortValue).map((alias) => (
              <tr key={alias.id}>
                <td>{alias.name || <span className="cell-sub">any name</span>}</td>
                <td>{alias.addressContains || <span className="cell-sub">any address</span>}</td>
                <td>{alias.note || '—'}</td>
                <td>
                  {formatDateTime(alias.createdUtc)}
                  {alias.createdByEmail ? <span className="cell-sub">{alias.createdByEmail}</span> : null}
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-link btn-danger"
                    disabled={aliasBusy !== null}
                    onClick={() => deleteAlias(alias)}
                  >
                    {aliasBusy === alias.id ? 'Removing…' : 'Remove'}
                  </button>
                </td>
              </tr>
            ))}
            <tr>
              <td>
                <TextField label="" value={newAliasName} onChange={setNewAliasName} placeholder="Acme Holdings Ltd" />
              </td>
              <td>
                <TextField
                  label=""
                  value={newAliasAddress}
                  onChange={setNewAliasAddress}
                  placeholder="120 King Street, or just M5H 1A9"
                />
              </td>
              <td>
                <TextField label="" value={newAliasNote} onChange={setNewAliasNote} placeholder="Old office — chase Acme" />
              </td>
              <td />
              <td>
                <button type="button" className="btn btn-secondary" disabled={aliasBusy !== null} onClick={addAlias}>
                  {aliasBusy === 'add' ? 'Adding…' : 'Add'}
                </button>
              </td>
            </tr>
          </tbody>
        </TableWrap>
        <p className="field-hint">
          Every field you fill in has to match. A name on its own accepts that name at any address; a
          name and an address fragment accept only that pairing. Leave both blank and nothing is
          added — an empty entry would accept every invoice.
        </p>
      </Panel>

      <Panel
        title="Pending senders"
        description="A sender at an approved domain whose exact address isn't approved yet — held instead of guessed at, since one domain can front more than one vendor. Pick the vendor to Add, or Ignore to dismiss."
      >
        {pendingState.error ? <InlineError message={pendingState.error} /> : null}
        <TableWrap busy={pendingState.loading} tableClassName="data-table-sortable">
          <colgroup>
            <col style={pendingTable.columnStyle('from')} />
            <col style={pendingTable.columnStyle('domain')} />
            <col style={pendingTable.columnStyle('subject')} />
            <col style={pendingTable.columnStyle('received')} />
            <col style={pendingTable.columnStyle('vendor')} />
            <col />
          </colgroup>
          <thead>
            <tr>
              <SortableTh
                label="From"
                sortKey="from"
                currentSortKey={pendingTable.sortKey}
                sortDir={pendingTable.sortDir}
                onSort={pendingTable.toggleSort}
                style={pendingTable.columnStyle('from')}
                onResizeStart={pendingTable.beginResize('from')}
              />
              <SortableTh
                label="Domain"
                sortKey="domain"
                currentSortKey={pendingTable.sortKey}
                sortDir={pendingTable.sortDir}
                onSort={pendingTable.toggleSort}
                style={pendingTable.columnStyle('domain')}
                onResizeStart={pendingTable.beginResize('domain')}
              />
              <SortableTh
                label="Subject"
                sortKey="subject"
                currentSortKey={pendingTable.sortKey}
                sortDir={pendingTable.sortDir}
                onSort={pendingTable.toggleSort}
                style={pendingTable.columnStyle('subject')}
                onResizeStart={pendingTable.beginResize('subject')}
              />
              <SortableTh
                label="Received"
                sortKey="received"
                currentSortKey={pendingTable.sortKey}
                sortDir={pendingTable.sortDir}
                onSort={pendingTable.toggleSort}
                style={pendingTable.columnStyle('received')}
                onResizeStart={pendingTable.beginResize('received')}
              />
              <SortableTh
                label="Vendor"
                sortKey="vendor"
                currentSortKey={pendingTable.sortKey}
                sortDir={pendingTable.sortDir}
                onSort={pendingTable.toggleSort}
                style={pendingTable.columnStyle('vendor')}
                onResizeStart={pendingTable.beginResize('vendor')}
              />
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {pendingTable.sort(pendingState.data ?? [], pendingSenderSortValue).map((item) => {
              const choice = pendingVendorChoice[item.pendingSenderId] ?? item.candidateVendors[0]?.qboVendorId ?? 'other';
              const isOther = choice === 'other';
              return (
                <tr key={item.pendingSenderId}>
                  <td>{item.fromAddress}</td>
                  <td>{item.domain}</td>
                  <td>{item.subject || '—'}</td>
                  <td>{item.receivedUtc ? formatDateTime(item.receivedUtc) : '—'}</td>
                  <td>
                    {item.candidateVendors.length > 0 ? (
                      <select
                        className="input"
                        value={choice}
                        onChange={(e) =>
                          setPendingVendorChoice((prev) => ({ ...prev, [item.pendingSenderId]: e.target.value }))
                        }
                      >
                        {item.candidateVendors.map((v) => (
                          <option key={v.qboVendorId} value={v.qboVendorId}>
                            {v.vendorName || v.qboVendorId}
                          </option>
                        ))}
                        <option value="other">Other vendor…</option>
                      </select>
                    ) : (
                      <span className="cell-sub">No vendor registered yet for this domain</span>
                    )}
                    {isOther || item.candidateVendors.length === 0 ? (
                      <>
                        <TextField
                          label=""
                          value={pendingOtherVendorId[item.pendingSenderId] ?? ''}
                          onChange={(value) =>
                            setPendingOtherVendorId((prev) => ({ ...prev, [item.pendingSenderId]: value }))
                          }
                          placeholder="QuickBooks vendor id"
                        />
                        <TextField
                          label=""
                          value={pendingOtherVendorName[item.pendingSenderId] ?? ''}
                          onChange={(value) =>
                            setPendingOtherVendorName((prev) => ({ ...prev, [item.pendingSenderId]: value }))
                          }
                          placeholder="Vendor name (optional)"
                        />
                      </>
                    ) : null}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={pendingBusy !== null}
                      onClick={() => addPendingSender(item)}
                    >
                      {pendingBusy === item.pendingSenderId ? 'Adding…' : 'Add'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-link"
                      disabled={pendingBusy !== null}
                      onClick={() => ignorePendingSender(item)}
                    >
                      Ignore
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </TableWrap>
        {!pendingState.loading && (pendingState.data?.length ?? 0) === 0 ? (
          <p className="field-hint">No senders are waiting for review.</p>
        ) : null}
      </Panel>
    </>
  );
}

/** Admin-only screen; the guard is repeated here so it holds however the page is rendered. */
export function EmailIntakePage(): JSX.Element {
  return (
    <RequireRole minRole={GatewayRole.Admin}>
      <EmailIntakeContent />
    </RequireRole>
  );
}
